import { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { SOAForm } from './SOAForm';
import { ApprovalStatuses } from './ApprovalStatuses';
import { getSOADetails } from '../../api';
import { SendSOAToTenant } from './SendSOAToTenant';

const customTheme = createTheme({
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
    h6: {
      fontFamily: '"Times New Roman", Times, serif',
      fontWeight: 700,
    },
    body1: {
      fontFamily: '"Times New Roman", Times, serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Times New Roman", Times, serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"Times New Roman", Times, serif',
      fontWeight: 700,
    },
  },
});

const GenerateSOAModal = ({
  isModalOpen,
  setIsModalOpen,
  formik,
  soaId,
  handleSubmit
}) => {
  const [soaData, setSoaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const fetchSOADetails = async () => {
      if (!soaId || !isModalOpen) return;
      setLoading(true);
      try {
        const { data } = await getSOADetails(soaId);
        setSoaData(data);
      } catch (err) {
        console.error('Error fetching SOA details:', err);
        setError('Failed to fetch SOA details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSOADetails();
  }, [soaId, isModalOpen]);

  const isEditable = !(
    !soaId ||
    (soaData && (
      soaData.approvals?.some(status => status.status === 'rejected') ||
      soaData.auditor_approval?.status === 'rejected'
    ))
  );

  const modalTitle = !soaId ? 'Create Statement of Account' : isEditable ? 'Edit Statement of Account' : 'Generate Statement of Account';

  const hasPropertyData = formik.values.property_details?.property_id;

  return (
    <Modal
      isOpen={isModalOpen}
      toggle={closeModal}
      size="xl"
      centered
      scrollable
      className="soa-modal"
      style={{ maxWidth: '90%' }}
    >
      <ModalHeader toggle={closeModal}>{modalTitle}</ModalHeader>
      <ModalBody className="overflow-x-hidden">
        <ThemeProvider theme={customTheme}>
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Loading SOA details...</div>
            </div>
          ) : error ? (
            <div className="text-danger text-center p-4">
              {error}
            </div>
          ) : hasPropertyData ? (
            <>
              <SOAForm
                formik={formik}
                soaId={soaId}
              />
              {soaId && <ApprovalStatuses soaId={soaId} soaData={soaData} />}
              {soaId && <SendSOAToTenant soaId={soaId} soaData={soaData} />}
            </>
          ) : (
            <div className="text-muted text-center p-4">
              Please select a property to {isEditable && !soaId ? 'create' : isEditable ? 'edit' : 'generate'} SOA.
            </div>
          )}
        </ThemeProvider>
      </ModalBody>
      <ModalFooter>
        {hasPropertyData && (
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={formik.isSubmitting || loading}
          >
            {formik.isSubmitting ? 'Submitting...' : !soaId ? 'Create SOA' : 'Update SOA'}
          </Button>
        )}
        <Button color="secondary" onClick={closeModal} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export { GenerateSOAModal };